import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Briefcase, Heart, TrendingUp, Users, DollarSign, Award, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { usePageTitle } from '../components/PageTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate URL slug from job title
const generateJobSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

const Careers = () => {
  usePageTitle('Careers | Lehigh Human Support Services');
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/jobs`);
      // Only show active jobs
      const activeJobs = response.data.filter(job => job.active !== false);
      setJobs(activeJobs);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    {
      icon: <Heart className="w-8 h-8" />,
      title: 'Meaningful Work',
      description: 'Make a real difference in people\'s lives every day'
    },
    {
      icon: <DollarSign className="w-8 h-8" />,
      title: 'Competitive Pay',
      description: 'Competitive salaries and performance incentives'
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Career Growth',
      description: 'Professional development and advancement opportunities'
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Comprehensive Training',
      description: 'Ongoing training and certification programs'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Supportive Team',
      description: 'Collaborative work environment with dedicated colleagues'
    },
    {
      icon: <Briefcase className="w-8 h-8" />,
      title: 'Flexible Schedules',
      description: 'Full-time, part-time, and flexible scheduling options'
    }
  ];

  // Navigate to dedicated job details page
  const handleJobClick = (job) => {
    const slug = generateJobSlug(job.title);
    navigate(`/careers/jobs/${slug}`);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-amber-50 to-white py-20 lg:py-32">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              Join Our Team
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed">
              Build a rewarding career making a meaningful difference in the lives of individuals with intellectual and developmental disabilities
            </p>
          </div>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">Why Work With Us</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join a team that values compassion, excellence, and making a difference
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="text-center p-8 hover:shadow-lg transition-shadow border-0 bg-gray-50">
                <CardContent className="space-y-4 pt-6">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto text-amber-600">
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">Open Positions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore our current opportunities and find your perfect role
            </p>
          </div>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-12 h-12 text-amber-500 animate-spin" />
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-12">
              <Briefcase className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">No Open Positions</h3>
              <p className="text-gray-600">Check back soon for new opportunities!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-8">
              {jobs.map((job) => (
                <Card 
                  key={job.id} 
                  className="hover:shadow-xl transition-shadow cursor-pointer border-0 bg-white"
                  onClick={() => handleJobClick(job)}
                  data-testid={`job-card-${job.id}`}
                >
                  <CardContent className="p-8 space-y-4">
                    <div className="flex items-start justify-between">
                      <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center text-amber-600">
                        <Briefcase className="w-6 h-6" />
                      </div>
                      <span className="px-3 py-1 bg-amber-50 text-amber-700 rounded-full text-sm font-medium">
                        {job.type}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{job.title}</h3>
                      <p className="text-gray-600">📍 {job.location}</p>
                    </div>
                    <div className="flex items-center justify-between pt-4 border-t">
                      <span className="text-amber-600 font-semibold">{job.salary}</span>
                      <Button className="bg-amber-500 hover:bg-amber-600">
                        View & Apply
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-4xl lg:text-5xl font-bold">Ready to Make a Difference?</h2>
            <p className="text-xl text-gray-300 leading-relaxed">
              Join Lehigh Human Support Services and start a fulfilling career helping others achieve their goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => window.scrollTo({ top: document.querySelector('section:nth-of-type(3)').offsetTop, behavior: 'smooth' })}
                className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-6 text-lg"
              >
                View Open Positions
              </Button>
              <Button 
                variant="outline" 
                className="border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg"
                onClick={() => window.location.href = '/contact'}
              >
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
